import ReactECharts from "echarts-for-react";
import _ from "lodash";
import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect } from "react";

const StackedHorizontalBar = (props) => {
  const {
    borderRadius,
    data,
    events,
    isScroll,
    isScrollEndLimit,
    isScrollStartLimit,
    labelConfig,
    legendSelect,
    onSprintClick,
    showLabel,
    showLegend,
    spaceBottom,
    spaceLeft,
    spaceRight,
    spaceTop,
    toolTipConfig,
    xField,
    yAxisDataArray,
    yField,
    customGrid,
    customItemStyle,
    yAxisLabelConfig,
    barWidth,
    customXAxis,
    yNameGap } = props;
  const [legendSelected, setLegendSelected] = useState({});
  const getStackOption = () => {
    let options = {
      tooltip: toolTipConfig ? { ...toolTipConfig } : {
        show: true,
        trigger: 'item',
      },
      legend: {
        orient: "horizontal",
        show: showLegend ? showLegend : true,
        icon: "circle",
        type: "scroll",
        left: 10,
        selectedMode: legendSelect ? legendSelect : true,
        selected: legendSelected,
        itemWidth: 13,
        itemHeight: 13,
        textStyle: {
          fontSize: 13,
        },
      },
      grid: customGrid || {
        top: spaceTop ? spaceTop : "10%",
        left: spaceLeft ? spaceLeft : "4%",
        right: spaceRight ? spaceRight : "2%",
        bottom: spaceBottom ? spaceBottom : "15%",
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: xField,
        nameGap: yNameGap ? yNameGap : 30,
        show: true,
        axisLine: {
          show: true,
        },
        axisLabel: {
          show: true,
        },
        nameLocation: "middle",
        ...(customXAxis || {}),
      },
      yAxis: {
        type: 'category',
        show: true,
        name: yField,
        nameLocation: "middle",
        data: yAxisDataArray,
        barGap: '30%',
        nameGap: yNameGap ? yNameGap : 16,
        axisLine: {
          show: true,
        },
        axisTick: {
          show: false,
        },
        axisLabel: yAxisLabelConfig || {
          formatter: function (value) {
            if (value.length > 7) {
              return value.substring(0, 7) + '...';
            }
            return value;
          }
        }
      },
      series: data.map((seriesData, index) => ({
        name: seriesData.name,
        type: 'bar',
        barWidth: barWidth ? barWidth : undefined,
        barCategoryGap: '40%',
        stack: 'total',
        label: showLabel
          ? {
            show: true,
            position: "inside",
            fontSize: 11,
            fontWeight: 600,
            color: "#fff",
            formatter: (params) => (params.value > 0 ? params.value : ""),
            ...labelConfig,
          }
          : { show: false },
        itemStyle: {
          borderRadius: borderRadius ? [5, 5, 5, 5] : [0, 0, 0, 0],
          color: seriesData.color,
          ...(customItemStyle || {}),
        },
        data: seriesData.data
      }))
    };
    if (isScroll) {
      options.dataZoom = [
        //set the width,yAxisIndex,left,orient to vertical
        {
          type: 'slider',
          start: isScrollStartLimit ? isScrollStartLimit : 0,
          end: isScrollEndLimit ? isScrollEndLimit : 30,
          width: 10,
          showDetail: false,
          yAxisIndex: 0,
          right: '0%',
          handleStyle: {
            color: "#00a3ff",
          },
          dataBackground: {
            lineStyle: {
              color: "#00a3ff"
            },
            areaStyle: {
              color: 'rgba(255, 0, 0, 0.1)'
            }
          },
          orient: 'vertical',
        },
      ];

    }
    return options;
  };

  const handleChartClick = (params) => {
    const clickedSprintIndex = params.dataIndex;
    const clickedissueIndex = params.componentIndex;
    const seriesName = params.seriesName;
    const clickedSprint = yAxisDataArray[clickedSprintIndex];

    if (onSprintClick) {
      onSprintClick(params, clickedSprint, seriesName, clickedissueIndex);
    }
  };

  const legendSelectedRef = useRef(legendSelected);

  let chartInstance = null;

  const handleChartReady = (chart) => {
    chartInstance = chart;

  };
  const handleLegendSelectChanged = (params) => {
    if (params.selected !== legendSelectedRef.current) {
      legendSelectedRef.current = params.selected;
    }
    const selectedValues = Object.values(params.selected);
    const allFalse = selectedValues.every(value => value === false);
    if (allFalse) {
      const YFilledHide = {
        yAxis: {
          type: 'category',
          show: false,
          name: yField,
          nameLocation: "middle",
          data: yAxisDataArray,
          barGap: '30%',
          nameGap: yNameGap ? yNameGap : 16,
          axisLine: {
            show: true,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            formatter: function (value) {
              if (value.length > 5) {
                return value.substring(0, 5) + '...';
              }
              return value;
            }
          }
        },
      };

      if (chartInstance) {
        chartInstance.setOption(YFilledHide);
      }
    }
    else {
      const YFilledShow = {
        yAxis: {
          show: true,
          type: 'category',
          name: yField,
          nameLocation: "middle",
          data: yAxisDataArray,
          barGap: '30%',
          nameGap: yNameGap ? yNameGap : 16,
          axisLine: {
            show: true,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            formatter: function (value) {
              if (value.length > 5) {
                return value.substring(0, 5) + '...';
              }
              return value;
            }
          }
        },
      };

      if (chartInstance) {
        chartInstance.setOption(YFilledShow);
      }
    }
  };

  const onEvents = {
    'click': handleChartClick,
    'legendselectchanged': handleLegendSelectChanged,
  };

  useEffect(() => {
    if (props.events && props.events.selected) {
      setLegendSelected(props.events.selected);
    }
  }, [props.events]);

  return <ReactECharts option={getStackOption()} style={{ height: '481px' }} onEvents={events ? onEvents : ""} onChartReady={handleChartReady} />;
};


function areEqual(prevProps, nextProps) {
  if (_.isEqual(prevProps.data, nextProps.data) &&
    _.isEqual(prevProps.yField, nextProps.yField) &&
    _.isEqual(prevProps.xField, nextProps.xField) &&
    _.isEqual(prevProps.yNameGap, nextProps.yNameGap) &&
    _.isEqual(prevProps.spaceTop, nextProps.spaceTop) &&
    _.isEqual(prevProps.spaceLeft, nextProps.spaceLeft) &&
    _.isEqual(prevProps.spaceRight, nextProps.spaceRight) &&
    _.isEqual(prevProps.color, nextProps.color) &&
    _.isEqual(prevProps.yAxisDataArray, nextProps.yAxisDataArray) &&
    _.isEqual(prevProps.isScrollEndLimit, nextProps.isScrollEndLimit) &&
    _.isEqual(prevProps.isScrollStartLimit, nextProps.isScrollStartLimit) &&
    _.isEqual(prevProps.showLegend, nextProps.showLegend)) {
    return true;
  } else {
    return false;
  }
}

StackedHorizontalBar.propTypes = {
  barWidth: PropTypes.number, // If you're using it elsewhere (not in the snippet)
  borderRadius: PropTypes.number,
  color: PropTypes.array, // Assuming this is used as a color array for bars
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      color: PropTypes.string,
      data: PropTypes.array
    })
  ).isRequired,
  events: PropTypes.object,
  isScroll: PropTypes.bool,
  isScrollEndLimit: PropTypes.number,
  isScrollStartLimit: PropTypes.number,
  labelConfig: PropTypes.object,
  legendSelect: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  showLabel: PropTypes.bool,
  onSprintClick: PropTypes.func,
  showLegend: PropTypes.bool,
  spaceBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  spaceLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  spaceRight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  spaceTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sprintTotalCount: PropTypes.number, // If you're using it in other code
  toolTipConfig: PropTypes.object,
  toolTipData: PropTypes.array, // If used
  xField: PropTypes.string,
  yAxisData: PropTypes.array, // If used
  yAxisDataArray: PropTypes.arrayOf(PropTypes.string).isRequired,
  yAxisLabelConfig: PropTypes.object,
  yAxisTextColor: PropTypes.string, // If used
  yField: PropTypes.string,
  yNameGap: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default React.memo(StackedHorizontalBar, areEqual);


